/**
 * Raw Data carry — re-attach a batch's imported instrument raw data onto reopen, a
 * port of OG `SpreadsheetEntryController.ReAttachRawData` (BTLIMS :31535).
 *
 * OG's re-open copies the batch's saved raw data into the workbook's "Raw Data" sheet
 * (the merge itself leaves that sheet header-only — a NEW batch has no raw data). AI
 * re-merges from tables, so the merged "Raw Data" sheet is likewise blank; this
 * re-attaches it the same way from the persisted `dbo.RawDataAttachment` rows.
 *
 * TWO OG branches, both handled here (`carryRawData` dispatches on the template):
 *   • SINGLE-instrument (else branch, :31588) — one shared raw file → copy its first
 *     worksheet. OG `LoadDocument(content, ResolveFormat(ext))` reads `.xlsx/.xls/.csv/.txt`;
 *     AI reads `.xlsx` with ExcelJS (formatting preserved) and `.xls/.csv/.txt` with SheetJS
 *     (values only — OG's `CopyFrom` off a raw instrument dump is likewise value-shaped).
 *   • MULTIPLE-instruments ("Raw Data Summary", :31539) — OG mail-merges the per-sample
 *     (SysSampleCode, SheetName) rows into the DevExpress "Raw Data Summary" TEMPLATE. AI has
 *     no client-side DevExpress mail-merge engine, so this builds the SAME summary in substance
 *     — a "Raw Data" sheet listing each sample and its raw-data file — from the persisted rows
 *     (the actual per-sample files stay reachable via the RawData preview column). A documented
 *     adaptation: the information is faithful; the DevExpress template's exact layout is not.
 *
 * SHEET NAME: "Raw Data" (ReAttachRawData), except when the workbook's formulas read OG's IMPORT
 * sheet "RawDataSheet" (PFAS_533_Waters G2:H4) — then the file is carried under that name, as it
 * sits in every OG blob of that template (`rawSheetNameFor`, 2026-09-26).
 *
 * Best-effort throughout: any miss leaves the workbook exactly as merged.
 */

import { copySheet } from './sdmsMailMerge';
import { qcBatchApi } from './qcBatch.api';
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';
import { api } from '../../api/client';

interface RawDataFileInfo {
  sysSampleCode: string | null;
  fileOid: string | null;
  fileName: string | null;
}
interface RawDataApiInfo {
  isMultiInstrument: boolean;
  files: RawDataFileInfo[];
}

/** One sample → its raw-data file, for the multi-instrument summary sheet. */
export interface RawDataSummaryRow {
  sysSampleCode: string;
  fileName: string;
}

type RawDataCarry =
  | { mode: 'single'; fileOid: string; fileName: string | null }
  | { mode: 'multi'; rows: RawDataSummaryRow[] }
  | null;

/** The raw-data extension → how we read it. OG `ResolveFormat` (:31651) maps the same four. */
function rawFormat(fileName?: string | null): 'xlsx' | 'xls' | 'csv' | 'txt' | 'unknown' {
  const ext = (fileName ?? '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  if (ext === 'xlsx') return 'xlsx';
  if (ext === 'xls') return 'xls';
  if (ext === 'csv') return 'csv';
  if (ext === 'txt') return 'txt';
  return 'unknown';
}

/** Fetch the batch's raw-data availability and shape it into the carry to perform:
 *  the newest single file, or the distinct per-sample rows for a MultipleInstruments
 *  template. Null when the batch has no raw data (or the fetch fails). */
async function fetchBatchRawData(batchOid: string): Promise<RawDataCarry> {
  try {
    const res = await api.get<{ success: boolean; data: RawDataApiInfo }>(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/raw-data`,
    );
    const info = res.data?.data;
    if (!info || !info.files?.length) return null;
    if (info.isMultiInstrument) {
      // Distinct (SysSampleCode, FileName) — OG builds dtRawData the same way (:31549).
      const seen = new Set<string>();
      const rows: RawDataSummaryRow[] = [];
      for (const f of info.files) {
        const code = (f.sysSampleCode ?? '').trim();
        const name = (f.fileName ?? '').trim();
        if (!code || !name) continue;
        const key = `${code} ${name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({ sysSampleCode: code, fileName: name });
      }
      return rows.length ? { mode: 'multi', rows } : null;
    }
    const f = info.files[0];
    return f?.fileOid ? { mode: 'single', fileOid: f.fileOid, fileName: f.fileName ?? null } : null;
  } catch (e) {
    console.warn('[SDMS] raw-data info fetch failed', e);
    return null;
  }
}

/** Back-compat single-file lookup (retained for callers/tests): the newest raw file for a
 *  single-instrument batch, or null for a MultipleInstruments template / no raw data. */
export async function fetchBatchRawDataFile(
  batchOid: string,
): Promise<{ fileOid: string; fileName: string | null } | null> {
  const info = await fetchBatchRawData(batchOid);
  return info?.mode === 'single' ? { fileOid: info.fileOid, fileName: info.fileName } : null;
}

/**
 * Re-attach the batch's raw data onto `currentBuf`, dispatching on the template
 * (single-instrument file vs multi-instrument summary). Returns a new buffer, or the
 * original unchanged when there is nothing to carry / a step fails. This is the entry
 * point both reopen paths call.
 */
export async function carryRawData(
  currentBuf: ArrayBuffer, batchOid: string,
  /** The batch's run order (SysSampleCodes as the merge records list them, first appearance) —
   *  OG's multi-instrument summary lists the samples in SEQUENCE order (its dtRawData is a DISTINCT
   *  over dtsample, :31463), not by upload time. Files for codes outside the sequence follow, oldest
   *  first. */
  sequence?: readonly string[],
): Promise<ArrayBuffer> {
  const info = await fetchBatchRawData(batchOid);
  if (!info) return currentBuf;
  if (info.mode === 'multi') {
    const template = await rawDataSummaryTemplate();
    return mergeRawDataSummary(currentBuf, orderBySequence(info.rows, sequence), template);
  }
  return embedRawDataSheet(currentBuf, info.fileOid, info.fileName);
}

/** What `carryRawDataIntoWorkbook` downloads, fetched ahead of it: the batch's raw-data list, then the single
 *  instrument file or the multi-instrument summary template. */
export interface RawDataPrefetch { info: RawDataCarry; raw: ArrayBuffer | null; template: ArrayBuffer | null }

/** Start the raw-data downloads now (speed, 2026-10-01): the reopen calls this together with the batch's data
 *  fetch, so the list request, the file download and loading the SheetJS reader overlap the server's merge-data
 *  work instead of following the merge (PFAS: ~0.3 s step). Only the downloads move — reading the file into the
 *  sheet stays in the open chain, unchanged. */
export async function prefetchRawDataCarry(batchOid: string): Promise<RawDataPrefetch | null> {
  const info = await fetchBatchRawData(batchOid);
  if (!info) return null;
  if (info.mode === 'multi') return { info, raw: null, template: await rawDataSummaryTemplate() };
  const fmt = rawFormat(info.fileName);
  if (fmt === 'xls' || fmt === 'csv' || fmt === 'txt') void import('xlsx').catch(() => undefined);   // warm the reader
  return { info, raw: await downloadRawDataFile(info.fileOid), template: null };
}

/** `carryRawData` on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns true
 *  when the raw-data sheet was put into `cur`; false leaves it untouched. */
export async function carryRawDataIntoWorkbook(
  cur: import('exceljs').Workbook, batchOid: string, sequence?: readonly string[],
  /** The downloads already started by prefetchRawDataCarry (same batch); absent = download them here. */
  prefetched?: Promise<RawDataPrefetch | null> | null,
): Promise<boolean> {
  const pre = prefetched ? await prefetched.catch(() => null) : undefined;
  const info = pre !== undefined ? (pre?.info ?? null) : await fetchBatchRawData(batchOid);
  if (!info) return false;
  if (info.mode === 'multi') {
    const template = pre ? pre.template : await rawDataSummaryTemplate();
    return mergeRawDataSummaryIntoWorkbook(cur, orderBySequence(info.rows, sequence), template);
  }
  const raw = pre ? pre.raw : await downloadRawDataFile(info.fileOid);
  return raw ? mergeRawDataIntoWorkbook(cur, raw, info.fileName) : false;
}

/** OG's "Raw Data Summary" template (SpreadSheetBuilder_TemplateInfo by name) — fetched once per
 *  session; null when the DB has none (the plain two-column fallback is used then). */
let _rawSummaryTpl: Promise<ArrayBuffer | null> | null = null;
export function rawDataSummaryTemplate(): Promise<ArrayBuffer | null> {
  if (!_rawSummaryTpl) {
    _rawSummaryTpl = qcBatchApi.sdmsTemplateByName(RAW_DATA_SUMMARY_TEMPLATE).catch(e => {
      console.warn('[SDMS] raw-data summary template fetch failed', e);
      _rawSummaryTpl = null;
      return null;
    });
  }
  return _rawSummaryTpl;
}
export const RAW_DATA_SUMMARY_TEMPLATE = 'Raw Data Summary';

/** Rows in the batch's sequence order (first appearance of the code), unknown codes after in the
 *  order given REVERSED (the API lists files newest-first; OG's fallback is oldest-first). */
export function orderBySequence(rows: readonly RawDataSummaryRow[], sequence?: readonly string[]): RawDataSummaryRow[] {
  const pos = new Map<string, number>();
  (sequence ?? []).forEach((c, i) => { const k = c.trim().toUpperCase(); if (!pos.has(k)) pos.set(k, i); });
  const key = (r: RawDataSummaryRow) => r.sysSampleCode.trim().toUpperCase();
  const known = rows.filter(r => pos.has(key(r))).sort((a, b) => pos.get(key(a))! - pos.get(key(b))!);
  const unknown = rows.filter(r => !pos.has(key(r))).reverse();
  return [...known, ...unknown];
}

/**
 * Single-instrument: copy the batch's raw-data file into the workbook as the "Raw Data"
 * sheet, replacing the merge's header-only one. Returns a new buffer, or the original
 * unchanged when the file can't be fetched / read. CF `text=` attrs are re-repaired so
 * the blob still opens in OG (see sdmsCfTextRepair.ts).
 */
export async function embedRawDataSheet(
  currentBuf: ArrayBuffer, fileOid: string, fileName?: string | null,
): Promise<ArrayBuffer> {
  const raw = await downloadRawDataFile(fileOid);
  return raw ? mergeRawDataWorkbook(currentBuf, raw, fileName) : currentBuf;
}

/** The batch's raw instrument file bytes, or null when it is missing / empty / the download fails. */
async function downloadRawDataFile(fileOid: string): Promise<ArrayBuffer | null> {
  let raw: ArrayBuffer;
  try {
    const res = await api.get<ArrayBuffer>(
      `/api/v1/analysis/result-validation/attachments/${encodeURIComponent(fileOid)}/download`,
      { responseType: 'arraybuffer' },
    );
    raw = res.data;
    if (!raw || raw.byteLength === 0) return null;
  } catch (e) {
    console.warn('[SDMS] raw-data download failed', e);
    return null;
  }
  return raw;
}

/** Raw text-file bytes → string (UTF-8, BOM stripped). */
function decodeText(bytes: ArrayBuffer): string {
  return new TextDecoder('utf-8').decode(bytes).replace(/^﻿/, '');
}

/** OG autofits the Raw Data columns after the copy (`Columns.AutoFit`, :31978-31987). ExcelJS has no
 *  autofit, so each column is sized to its longest displayed text (Excel width ≈ characters). */
function autoFitColumns(ws: import('exceljs').Worksheet): void {
  const widths = new Map<number, number>();
  ws.eachRow({ includeEmpty: false }, row => {
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const text = cell.text ?? '';
      const longest = Math.max(...String(text).split(/\r?\n/).map(l => l.length));
      if (longest > (widths.get(colNumber) ?? 0)) widths.set(colNumber, longest);
    });
  });
  for (const [col, len] of widths) ws.getColumn(col).width = Math.min(Math.max(len + 2, 8.43), 100);
}

/** The sheet OG's Import Files adds for an .xlsx/.csv instrument file (SpreadsheetEntryController
 *  :24957 / :24968 — a copy of the file's FIRST worksheet). OG re-adds it after the import re-bind for
 *  the "PFAS_533" data transfer (:11975), so it is in every PFAS_533_Waters blob, and that template's
 *  G2:H4 ISTD-response averages read it (`AVERAGEIFS(RawDataSheet!T:T, …)`). */
export const OG_IMPORT_RAW_SHEET = 'RawDataSheet';
const RAW_DATA_SHEET = 'Raw Data';
/** The multi-instrument summary sheet (OG "Raw Data") — the name the import refresh redraws in place. */
export const RAW_DATA_SUMMARY_SHEET = RAW_DATA_SHEET;

/** Where the carried raw file goes: OG's import sheet when the workbook's formulas READ a sheet by
 *  that name it does not have (the template was authored against the import copy — derived from the
 *  template, never a template list), else OG ReAttachRawData's "Raw Data". */
export function rawSheetNameFor(wb: import('exceljs').Workbook): string {
  const has = (n: string) => wb.worksheets.some(w => w.name.toLowerCase() === n.toLowerCase());
  if (has(OG_IMPORT_RAW_SHEET)) return OG_IMPORT_RAW_SHEET;
  const re = new RegExp(`(^|[^A-Za-z0-9_.])'?${OG_IMPORT_RAW_SHEET}'?!`, 'i');
  let reads = false;
  for (const ws of wb.worksheets) {
    if (reads) break;
    ws.eachRow({ includeEmpty: false }, row => {
      if (reads) return;
      row.eachCell({ includeEmpty: false }, cell => {
        const v = cell.value as { formula?: string; sharedFormula?: string } | null;
        const f = v && typeof v === 'object' ? (v.formula ?? v.sharedFormula) : undefined;
        if (typeof f === 'string' && re.test(f)) reads = true;
      });
    });
  }
  return reads ? OG_IMPORT_RAW_SHEET : RAW_DATA_SHEET;
}

/** Does this workbook's formulas read OG's import sheet "RawDataSheet" (PFAS_533)? Only then does an
 *  import add it (addImportRawDataSheet). Remembered per buffer — the template is checked on every import. */
const readsRawSheetCache = new WeakMap<ArrayBuffer, boolean>();
export async function readsImportRawSheet(buf: ArrayBuffer): Promise<boolean> {
  const hit = readsRawSheetCache.get(buf);
  if (hit !== undefined) return hit;
  let reads = true;   // unreadable → let addImportRawDataSheet decide on the live workbook, as before
  try {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf.slice(0));
    reads = rawSheetNameFor(wb) === OG_IMPORT_RAW_SHEET;
  } catch (e) {
    console.warn('[SDMS] RawDataSheet check skipped', e);
  }
  readsRawSheetCache.set(buf, reads);
  return reads;
}

/** Replace the workbook's raw-data sheet (or add it), returning nothing — the caller writes the workbook
 *  (CF `text=` attrs are restored by writeSdmsWorkbook). Shared by the .xlsx copy and the .xls/.csv/.txt value fill. */
function putRawDataSheet(
  cur: import('exceljs').Workbook, fill: (dst: import('exceljs').Worksheet) => void,
  sheetName: string = RAW_DATA_SHEET,
): void {
  const existing = cur.worksheets.find(w => w.name.toLowerCase() === sheetName.toLowerCase());
  if (existing) cur.removeWorksheet(existing.id);
  const dst = cur.addWorksheet(sheetName);
  fill(dst);
  dst.state = 'visible';
  // OG keeps its hidden "Qualifier" list sheet LAST (MoveToEnd, SpreadsheetEntryController :3889-3892), so a sheet
  // added after it goes in front of it (OG vs AI comparison 2026-09-28, point 12).
  const qualifier = cur.worksheets.find(w => w.name === 'Qualifier' && w.id !== dst.id);
  if (qualifier) {
    const order = (w: import('exceljs').Worksheet) => w as unknown as { orderNo: number };
    const last = Math.max(...cur.worksheets.map(w => order(w).orderNo));
    order(dst).orderNo = order(qualifier).orderNo;
    order(qualifier).orderNo = last + 1;
  }
}

/**
 * Put the raw file into `currentBuf` as the "Raw Data" sheet (replacing the merge's header-only
 * one) — or as OG's import sheet "RawDataSheet" when the workbook's formulas read that sheet
 * (`rawSheetNameFor`; then the file's FIRST worksheet, as OG's import copies it). `.xlsx` copies the sheet (formatting kept);
 * `.xls/.csv/.txt` are read with SheetJS and written as VALUES (OG's `CopyFrom` off a
 * raw instrument dump is likewise value-shaped). With NO `fileName` the `.xlsx` reader is
 * used and non-xlsx bytes leave the workbook unchanged (the pre-2026-09 contract).
 * Exported for unit testing (no network).
 */
export async function mergeRawDataWorkbook(
  currentBuf: ArrayBuffer, rawBytes: ArrayBuffer, fileName?: string | null,
  /** Only add the sheet when the workbook has none of that name yet (OG's import:
   *  `Contains("RawDataSheet") == false`, :24957 — the FIRST imported file wins). */
  onlyIfAbsent = false,
): Promise<ArrayBuffer> {
  try {
    const cur = await loadSdmsWorkbook(currentBuf);
    return (await mergeRawDataIntoWorkbook(cur, rawBytes, fileName, onlyIfAbsent)) ? await writeSdmsWorkbook(cur) : currentBuf;
  } catch (e) {
    console.warn('[SDMS] raw-data sheet carry skipped (unreadable format?)', e);
    return currentBuf;
  }
}

/** `mergeRawDataWorkbook` on a workbook already in memory. Returns true when the sheet was put into
 *  `cur`; false (nothing to add, or the raw file is unreadable) leaves it untouched. */
export async function mergeRawDataIntoWorkbook(
  cur: import('exceljs').Workbook, rawBytes: ArrayBuffer, fileName?: string | null, onlyIfAbsent = false,
): Promise<boolean> {
  const fmt = rawFormat(fileName);
  try {
    const ExcelJS = (await import('exceljs')).default;

    // .xls / .csv / .txt — ExcelJS can't read these; SheetJS reads all three → values.
    // OG ReAttachRawData (:31933-31987) loads with ResolveFormat → DocumentFormat.Xls / Csv / Text
    // and default import options: Csv splits on COMMA, Text on TAB (a comma .txt stays one column,
    // as in OG). SheetJS would otherwise sniff the delimiter, so it is forced here.
    if (fmt === 'xls' || fmt === 'csv' || fmt === 'txt') {
      const XLSX = await import('xlsx');
      const src = fmt === 'xls'
        ? XLSX.read(new Uint8Array(rawBytes), { type: 'array' })
        : XLSX.read(decodeText(rawBytes), { type: 'string', FS: fmt === 'txt' ? '\t' : ',', raw: false });
      // OG copies the ACTIVE worksheet (:31952). SheetJS 0.18.5 parses the .xls Window1 record but
      // drops its active-tab index, so .xls takes the first sheet — instrument .xls dumps are
      // single-sheet in practice. csv/txt only ever have one sheet.
      const name = src.SheetNames[0];
      if (!name) return false;
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(src.Sheets[name]!, { header: 1, blankrows: true });
      if (!aoa.length) return false;
      const sheetName = rawSheetNameFor(cur);
      if (onlyIfAbsent && cur.worksheets.some(w => w.name.toLowerCase() === sheetName.toLowerCase())) return false;
      putRawDataSheet(cur, dst => {
        for (const row of aoa) dst.addRow(Array.isArray(row) ? row : [row]);
        autoFitColumns(dst);
      }, sheetName);
      return true;
    }

    // .xlsx (or unknown, best-effort) — copy the ACTIVE worksheet (OG :31952) with formatting.
    const src = new ExcelJS.Workbook();
    await src.xlsx.load(rawBytes);      // throws for .xls / .csv / .txt → caught below (no-op)
    const sheetName = rawSheetNameFor(cur);
    if (onlyIfAbsent && cur.worksheets.some(w => w.name.toLowerCase() === sheetName.toLowerCase())) return false;
    // OG's import copies Worksheets[0] (:24959); its re-attach copies the ACTIVE sheet (:31952).
    const activeTab = sheetName === OG_IMPORT_RAW_SHEET ? 0
      : (src.views?.[0] as { activeTab?: number } | undefined)?.activeTab ?? 0;
    const srcWs = src.worksheets[activeTab] ?? src.worksheets[0];
    if (!srcWs) return false;
    putRawDataSheet(cur, dst => { copySheet(srcWs, dst); autoFitColumns(dst); }, sheetName);
    return true;
  } catch (e) {
    console.warn('[SDMS] raw-data sheet carry skipped (unreadable format?)', e);
    return false;
  }
}

/**
 * Import Files: OG adds the imported .xlsx/.csv file to the document as a sheet when it is not there
 * yet (SpreadsheetEntryController import :26336 / :26347 — a copy of the file's FIRST worksheet, the
 * first imported file wins) and keeps it through the re-bind (Snapshot/RestoreRawDataSheet), so after
 * every import the analyst sees the instrument data in the workbook. The sheet is OG's "Raw Data"; a
 * template whose formulas read "RawDataSheet" (PFAS_533's G2:H4 ISTD averages,
 * `AVERAGEIFS(RawDataSheet!T:T, …)`) gets it under that name instead (rawSheetNameFor). Returns the
 * workbook with the sheet added, else null — the sheet is already there, or the file is not
 * .xlsx/.csv (OG adds nothing for .xls / .txt).
 */
export async function addImportRawDataSheet(
  currentBuf: ArrayBuffer, rawBytes: ArrayBuffer, fileName: string,
): Promise<ArrayBuffer | null> {
  if (!/\.(xlsx|csv)$/i.test(fileName.trim())) return null;
  try {
    const cur = await loadSdmsWorkbook(currentBuf);
    const sheetName = rawSheetNameFor(cur);
    if (cur.worksheets.some(w => w.name.toLowerCase() === sheetName.toLowerCase())) return null;
    // One parse: the checks above only READ this workbook (mergeRawDataWorkbook used to parse it again).
    return (await mergeRawDataIntoWorkbook(cur, rawBytes, fileName, true)) ? await writeSdmsWorkbook(cur) : null;
  } catch (e) {
    console.warn('[SDMS] import raw-data sheet skipped', e);
    return null;
  }
}

/**
 * Multi-instrument: build the "Raw Data" SUMMARY sheet listing each sample and its
 * raw-data file (OG mail-merges the same (SysSampleCode, SheetName) rows into the
 * "Raw Data Summary" template; AI has no client mail-merge, so it writes the summary
 * directly — see the file header). Returns `currentBuf` unchanged when there are no
 * rows or the workbook can't be read. Exported for unit testing (no network).
 */
export async function mergeRawDataSummary(
  currentBuf: ArrayBuffer, rows: readonly RawDataSummaryRow[],
  /** OG's "Raw Data Summary" template: its "Raw Data" sheet (title row, blank row, a merged
   *  SysSampleCode / Raw Data File Name header, then ONE band row of FIELD("SYSSAMPLECODE") /
   *  FIELD("SHEETNAME") cells, each merged across its columns) is filled per row exactly as
   *  DevExpress mail-merge does — styles and merges repeated per record — and REPLACES the
   *  workbook's "Raw Data" sheet (OG AttachRawData :31497 CopyFrom). Without it (no such template
   *  on the DB) the plain two-column list is written instead. (parity class 5, 2026-09-23) */
  template?: ArrayBuffer | null,
): Promise<ArrayBuffer> {
  if (!rows.length) return currentBuf;
  try {
    const cur = await loadSdmsWorkbook(currentBuf);
    return (await mergeRawDataSummaryIntoWorkbook(cur, rows, template)) ? await writeSdmsWorkbook(cur) : currentBuf;
  } catch (e) {
    console.warn('[SDMS] raw-data summary carry skipped', e);
    return currentBuf;
  }
}

/** `mergeRawDataSummary` on a workbook already in memory. Returns true when the summary sheet was put
 *  into `cur`; false (no rows, or the summary template is unreadable) leaves it untouched. */
export async function mergeRawDataSummaryIntoWorkbook(
  cur: import('exceljs').Workbook, rows: readonly RawDataSummaryRow[], template?: ArrayBuffer | null,
): Promise<boolean> {
  if (!rows.length) return false;
  try {
    if (template) {
      const tpl = await loadSdmsWorkbook(template);
      const src = tpl.worksheets.find(w => w.name === 'Raw Data') ?? tpl.worksheets[0];
      if (src && fillSummaryTemplate(src, rows)) { putRawDataSheet(cur, dst => copySheet(src, dst)); return true; }
    }
    putRawDataSheet(cur, dst => {
      const header = dst.addRow(['Sys Sample Code', 'Raw Data File']);
      header.font = { bold: true };
      for (const r of rows) dst.addRow([r.sysSampleCode, r.fileName]);
      dst.getColumn(1).width = 28;
      dst.getColumn(2).width = 48;
    });
    return true;
  } catch (e) {
    console.warn('[SDMS] raw-data summary carry skipped', e);
    return false;
  }
}

const SUMMARY_FIELD_RE = /FIELD\(\s*"([^"]+)"\s*\)/i;

/** Expand the summary template's single band row (the row holding FIELD() cells) into one row per
 *  record IN PLACE: the band row's cell values, styles, height and merged ranges are repeated for
 *  every record. Returns false when the sheet has no FIELD() row (not a summary template). */
export function fillSummaryTemplate(ws: import('exceljs').Worksheet, rows: readonly RawDataSummaryRow[]): boolean {
  let bandRow = 0;
  const fields = new Map<number, string>();   // col → field name (upper)
  ws.eachRow({ includeEmpty: false }, (row, r) => {
    if (bandRow) return;
    row.eachCell({ includeEmpty: false }, (cell, c) => {
      const v = cell.value as { formula?: string; sharedFormula?: string } | null;
      const f = v && typeof v === 'object' ? (v.formula ?? v.sharedFormula ?? '') : '';
      const m = SUMMARY_FIELD_RE.exec(String(f ?? ''));
      if (m) { fields.set(c, m[1]!.trim().toUpperCase()); bandRow = r; }
    });
  });
  if (!bandRow) return false;
  const valueFor = (field: string, rec: RawDataSummaryRow): string => {
    if (field === 'SYSSAMPLECODE') return rec.sysSampleCode;
    if (field === 'SHEETNAME' || field === 'FILENAME' || field === 'RAWDATAFILE') return rec.fileName;
    return '';
  };
  const band = ws.getRow(bandRow);
  const bandCells: Array<{ col: number; style: Partial<import('exceljs').Style> }> = [];
  band.eachCell({ includeEmpty: true }, (cell, c) => bandCells.push({ col: c, style: cell.style }));
  // Merged ranges that sit on the band row, as column spans to repeat per record.
  const merges = ((ws.model as { merges?: string[] }).merges ?? [])
    .map(m => /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(m))
    .filter((m): m is RegExpExecArray => !!m && Number(m[2]) === bandRow && Number(m[4]) === bandRow)
    .map(m => ({ from: m[1]!, to: m[3]! }));
  rows.forEach((rec, i) => {
    const r = bandRow + i;
    const row = ws.getRow(r);
    if (i > 0) {
      if (band.height) row.height = band.height;
      for (const bc of bandCells) ws.getCell(r, bc.col).style = { ...bc.style };
      for (const mg of merges) { try { ws.mergeCells(`${mg.from}${r}:${mg.to}${r}`); } catch { /* already merged */ } }
    }
    for (const [c, field] of fields) ws.getCell(r, c).value = valueFor(field, rec);
  });
  return true;
}
